import * as React from 'react'
import { useMotionValue, useTransform } from 'framer-motion'

export type SheetDirection = 'top' | 'bottom' | 'left' | 'right'

interface Point {
  x: number
  y: number
}

const DISMISS_AXIS: Record<SheetDirection, { axis: 'x' | 'y'; sign: 1 | -1 }> = {
  bottom: { axis: 'y', sign: 1 },
  top: { axis: 'y', sign: -1 },
  left: { axis: 'x', sign: -1 },
  right: { axis: 'x', sign: 1 },
}

const CLOSE_DISTANCE = 100
const CLOSE_VELOCITY = 0.3

function dismissDelta(direction: SheetDirection, start: Point, current: Point) {
  const { axis, sign } = DISMISS_AXIS[direction]
  const delta = axis === 'y' ? current.y - start.y : current.x - start.x
  return delta * sign
}

export function dragOffset(
  direction: SheetDirection,
  start: Point,
  current: Point,
  resistOpposite: boolean,
) {
  const distance = dismissDelta(direction, start, current)
  if (resistOpposite && distance <= 0) {
    return { x: 0, y: 0 }
  }
  const { axis, sign } = DISMISS_AXIS[direction]
  if (axis === 'y') {
    return { x: 0, y: distance * sign }
  }
  return { x: distance * sign, y: 0 }
}

export function dragDecision({
  direction,
  offset,
  duration,
  collapsedHeight,
  isCollapsed,
}: {
  direction: SheetDirection
  offset: Point
  duration: number
  collapsedHeight?: string
  isCollapsed: boolean
}) {
  const { axis, sign } = DISMISS_AXIS[direction]
  const travel = (axis === 'y' ? offset.y : offset.x) * sign
  const velocity = travel / duration
  const shouldClose = travel > CLOSE_DISTANCE || velocity > CLOSE_VELOCITY
  const shouldExpand = travel < -CLOSE_DISTANCE || velocity < -CLOSE_VELOCITY
  if (shouldClose && collapsedHeight && !isCollapsed) {
    return 'collapse'
  }
  if (shouldExpand && collapsedHeight && isCollapsed) {
    return 'expand'
  }
  if (shouldClose && !collapsedHeight) {
    return 'close'
  }
  return 'rest'
}

export function useSheetDrag({
  direction,
  collapsedHeight,
  isCollapsed,
  close,
  onCollapse,
  onExpand,
}: {
  direction: SheetDirection
  collapsedHeight?: string
  isCollapsed: boolean
  close?: () => void
  onCollapse?: () => void
  onExpand?: () => void
}) {
  const offsetX = useMotionValue(0)
  const offsetY = useMotionValue(0)
  const gesture = React.useRef({ dragging: false, x: 0, y: 0, time: 0 })
  const transform = useTransform(
    () => `translateX(${offsetX.get()}px) translateY(${offsetY.get()}px)`,
  )

  const onPointerDown = (event: React.PointerEvent) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    gesture.current = { dragging: true, x: event.clientX, y: event.clientY, time: Date.now() }
  }

  const onPointerMove = (event: React.PointerEvent) => {
    if (!gesture.current.dragging) {
      return
    }
    const next = dragOffset(
      direction,
      { x: gesture.current.x, y: gesture.current.y },
      { x: event.clientX, y: event.clientY },
      !collapsedHeight,
    )
    offsetX.set(next.x)
    offsetY.set(next.y)
  }

  const onPointerUp = (event: React.PointerEvent) => {
    event.currentTarget.releasePointerCapture(event.pointerId)
    const decision = dragDecision({
      direction,
      offset: { x: offsetX.get(), y: offsetY.get() },
      duration: Date.now() - gesture.current.time,
      collapsedHeight,
      isCollapsed,
    })
    if (decision === 'collapse') {
      onCollapse?.()
    } else if (decision === 'expand') {
      onExpand?.()
    } else if (decision === 'close') {
      close?.()
    }
    gesture.current.dragging = false
    offsetX.set(0)
    offsetY.set(0)
  }

  return { transform, onPointerDown, onPointerMove, onPointerUp }
}
